import type { IAppStores } from "./AppStores";
import { ResourcesFetcher } from "../network/fetchers/ResourcesFetcher";
import { ResourcesStore } from "../resources/ResourcesStore";
import { POW3Fetchers } from "../network/fetchers/POW3Fetchers";
import { NewsMenuStore } from "./header/news-menu/NewsMenuStore";
import { RichToolTipStore } from "./rich-tooltip/RichToolTipStore";
import { AboutUsDialogStore } from "./header/help-menu/about-us/AboutUsDialogStore";
import { HeartBeatFetcher } from "../network/fetchers/HeartBeatFetcher";
import { DefaultCurrentWindowStore } from "./window/DefaultCurrentWindowStore";
import { CurrentDocumentStore } from "./window/document/CurrentDocumentStore";
import { HeartBeatStore } from "./heartbeat/HeartBeatStore";
import { ProductInfoStore } from "./product-info/ProductInfoStore";
import { CookieSettingsStore } from "./cookie-banner/CookieSettingsStore";
import type { Language } from "../util/language/language";
import { ThirdPartySoftwareStore } from "./header/help-menu/about-us/ThirdPartySoftwareStore";
import type { LayoutViewModelModel } from "../../OpenApi/api";
import type { IFetcher } from "../util/io/Fetcher";
import { SessionStore } from "./session/SessionStore";
import { DicomUploadFetcher } from "./header/upload/DicomUploadFetcher";
import { UploadMenuStore } from "./header/upload/UploadMenuStore";
import { UploadDialogStore } from "./header/upload/UploadDialogStore";
import { UploadSourcesStore } from "./header/upload/UploadSourcesStore";
import { ServerInfoStore } from "./server-info/ServerInfoStore";
import { ExceptionlessStore, Logger } from "../util/exceptions/ExceptionlessStore";
import { ExceptionlessClientFactory } from "../util/exceptions/ExceptionlessClientFactory";
import { KnownExceptionsToIgnoreCatcher } from "../util/exceptions/KnownExceptionsToIgnoreCatcher";
import { NewsItemMapper } from "./header/news-menu/NewsItemMapper";
import { SystemDefaultsStore } from "./system-defaults/SystemDefaultsStore";
import { HomeReadingPairingStore } from "./header/home-reading/HomeReadingPairingStore";
import { Pow3SessionTracker } from "./heartbeat/Pow3SessionTracker";
import { HomeReadingFetcher } from "../network/fetchers/HomeReadingFetcher";
import { EnvironmentStore } from "./environments/EnvironmentStore";
import { CountryStore } from "./country/CountryStore";
import { GoogleAnalyticsStore } from "./google-analytics/GoogleAnalyticsStore";

export interface IAppStoresFactory {
	create(layoutViewModel: LayoutViewModelModel): Promise<IAppStores>;
}

export class AppStoresFactory implements IAppStoresFactory {
	constructor(
		private readonly fetcher: IFetcher,
		private readonly window: Window,
	) {}

	public async create(layoutViewModel: LayoutViewModelModel): Promise<IAppStores> {
		const { fetcher, window } = this;
		const { productInfo, sessionInfo, serverInfo, systemDefaultsInfo, popupInfo, countryInfo, googleAnalyticsInfo } = layoutViewModel;

		const currentWindowStore = new DefaultCurrentWindowStore();
		const currentDocumentStore = new CurrentDocumentStore(currentWindowStore);

		const pow3Fetchers = new POW3Fetchers(fetcher);
		const resourcesFetcher = new ResourcesFetcher(fetcher, productInfo.version);
		const resourcesPromise = resourcesFetcher.fetch();

		/* Error logging */
		const exceptionlessClient = ExceptionlessClientFactory.create(layoutViewModel.frontendExceptionlessInfo, ["Viewer", "App"]);
		const knownExceptionToIgnoreCatcher = new KnownExceptionsToIgnoreCatcher();
		const exceptionlessStore = new ExceptionlessStore(exceptionlessClient, knownExceptionToIgnoreCatcher);
		const logger = new Logger(exceptionlessStore);

		//get (current) environment information adn current country information from POW3
		const getEnvironmentAndCountryInfoPromise = pow3Fetchers.getEnvironmentAndCountryInfo();

		await Promise.all([resourcesPromise, getEnvironmentAndCountryInfoPromise]);

		const environmentAndCountryInfo = await getEnvironmentAndCountryInfoPromise;

		const resources = await resourcesPromise;
		const resourcesStore = new ResourcesStore(
			resources.resources as unknown as IResources,
			resources.language as Language,
			resources.possibleLanguages as Language[],
			resourcesFetcher,
		);

		const environmentStore = new EnvironmentStore(environmentAndCountryInfo);
		const countryStore = new CountryStore(environmentAndCountryInfo, countryInfo, resourcesStore);

		const richToolTipStore = new RichToolTipStore(currentWindowStore);

		const productInfoStore = new ProductInfoStore(productInfo, environmentStore, countryStore);
		const serverInfoStore = new ServerInfoStore(serverInfo);

		const newsItemMapper = new NewsItemMapper();
		const newsMenuStore = new NewsMenuStore(pow3Fetchers, newsItemMapper);

		const aboutUsDialogStore = new AboutUsDialogStore(countryStore, productInfoStore);
		const sessionStore = new SessionStore(sessionInfo, fetcher);
		const heartBeatFetcher = new HeartBeatFetcher(fetcher);
		const heartBeatStore = new HeartBeatStore(heartBeatFetcher, currentWindowStore, sessionStore);
		const pow3SessionTracker = new Pow3SessionTracker(sessionStore, currentDocumentStore, currentWindowStore, fetcher);

		const cookieSettingsStore = new CookieSettingsStore(sessionStore, popupInfo);
		const thirdPartySoftwareStore = new ThirdPartySoftwareStore();

		const googleAnalyticsStore = new GoogleAnalyticsStore(googleAnalyticsInfo, cookieSettingsStore, currentWindowStore);

		const dicomUploadFetcher = new DicomUploadFetcher(fetcher);
		const uploadSourcesStore = new UploadSourcesStore(sessionStore);
		const uploadDialogStore = new UploadDialogStore(uploadSourcesStore, dicomUploadFetcher, googleAnalyticsStore, window);
		const uploadStore = new UploadMenuStore(uploadSourcesStore, uploadDialogStore);

		const systemDefaultsStore = new SystemDefaultsStore(systemDefaultsInfo);
		const homeReadingFetcher = new HomeReadingFetcher(fetcher);
		const homeReadingPairingStore = new HomeReadingPairingStore(systemDefaultsStore, sessionStore, homeReadingFetcher);

		return {
			productInfoStore,
			serverInfoStore,
			resourcesStore,
			richToolTipStore,
			newsMenuStore,
			environmentStore,
			countryStore,
			aboutUsDialogStore,
			heartBeatStore,
			pow3SessionTracker,
			currentWindowStore,
			currentDocumentStore,
			sessionStore,
			cookieSettingsStore,
			thirdPartySoftwareStore,
			uploadMenuStore: uploadStore,
			uploadDialogStore,
			systemDefaultsStore,
			logger,
			homeReadingPairingStore,
			googleAnalyticsStore,
		};
	}
}
